import { API_BASE_URL, API_KEY } from '../../../api/auth/authApi'
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth'

export interface VehicleCategory {
  id: number
  name: string
  slug: string
  is_active: boolean
  created_at?: string
}

export interface VehicleCategoryPayload {
  name: string
  slug?: string
  is_active?: boolean
}

export interface VehicleCategoryApiResponse<T = unknown> {
  success: boolean
  message: string
  data?: T
  warning?: string
}

const API_PATH_URL = `${API_BASE_URL}/api/admin`
const getHeaders = (): HeadersInit => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY)

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
  }
}

const getErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const result = await response.json().catch(() => null)

  if (result?.detail) {
    return result.detail
  }

  if (result?.message) {
    return result.message
  }

  if (typeof result === 'object' && result !== null) {
    const firstError = Object.values(result)[0]

    if (Array.isArray(firstError) && firstError.length > 0) {
      return String(firstError[0])
    }

    if (typeof firstError === 'string') {
      return firstError
    }
  }

  return fallback
}

/**
 * GET /api/admin/vehicle-categories/
 */
export async function apiGetVehicleCategories(): Promise<
  VehicleCategoryApiResponse<VehicleCategory[]>
> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-categories/`,
      {
        method: 'GET',
        headers: getHeaders(),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to load vehicle categories.',
        ),
      }
    }

    const result = await response.json()

    const data = Array.isArray(result)
      ? result
      : result?.results || result?.data || []

    return {
      success: true,
      message: 'Vehicle categories loaded successfully.',
      data,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

/**
 * POST /api/admin/vehicle-categories/
 */
export async function apiCreateVehicleCategory(
  payload: VehicleCategoryPayload,
): Promise<VehicleCategoryApiResponse<VehicleCategory>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-categories/`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to create vehicle category.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message: 'Vehicle category created successfully.',
      data: result?.data || result,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

/**
 * PUT /api/admin/vehicle-categories/:id/
 */
export async function apiUpdateVehicleCategory(
  id: number,
  payload: VehicleCategoryPayload,
): Promise<VehicleCategoryApiResponse<VehicleCategory>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-categories/${id}/`,
      {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to update vehicle category.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message: 'Vehicle category updated successfully.',
      data: result?.data || result,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

/**
 * DELETE /api/admin/vehicle-categories/:id/
 */
export async function apiDeleteVehicleCategory(
  id: number,
): Promise<VehicleCategoryApiResponse> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-categories/${id}/`,
      {
        method: 'DELETE',
        headers: getHeaders(),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to delete vehicle category.',
        ),
      }
    }

    return {
      success: true,
      message: 'Vehicle category deleted successfully.',
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}